import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import * as Yup from "yup";
import Select from "react-select";
import { toast } from "react-toastify";
import AsyncSelect from "react-select/async";
import DatePicker from "react-date-picker";
import { FormikProps, useFormik } from "formik";
import { Badge, Button, Form } from "react-bootstrap";
import usePatient from "../../../Hooks/usePatient";
import useEcwOrders from "../../../Hooks/useEcwOrders";
import EcwOrderInfo from "../InfoComponents/EcwOrderInfo";
import { ensurePeriod, toggleClass } from "../../../utils/commonUtils";
import CustomModal from "../../../components/customModal/CustomModal";
import {
  AddPatientFormInitialValues,
  CommonEcwComponentProps,
  OrderTypeLogos,
  OrderTypeOptions,
  EcwFormValues,
  EcwOrdersResponse,
  EcwOrder,
  genderOptions,
  EcwOrderResponse,
  RecordApiMessages,
} from "../EcwConstants";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import {
  setEcwOrderList,
  setEcwSelectedOrderInfo,
} from "../../../redux/ecwOrder/ecwOrderSlice";
import { useModulePermissions, PermissionModule, PermissionIdentifier } from "../../../utils/permissionUtils";

interface EcwBasicInfoFormProps extends CommonEcwComponentProps {
  formik: FormikProps<EcwFormValues>;
  recordApiMsgs: RecordApiMessages | null;
  ecwOrderRes: EcwOrderResponse | null;
  handleResetForm: (isReset?: boolean) => void;
}

const patientValidationSchema = Yup.object({
  firstName: Yup.string().required("First Name is Required"),
  lastName: Yup.string().required("Last Name is Required"),
  patientCode: Yup.string().required("Patient ID is Required"),
  dob: Yup.date().nullable(),
  gender: Yup.string().required("Gender is required"),
});

const EcwBasicInfoForm: React.FC<EcwBasicInfoFormProps> = ({
  formik,
  setLoading,
  recordApiMsgs,
  ecwOrderRes,
  handleResetForm,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [ecwOrders, setEcwOrders] = useState<EcwOrdersResponse>({});
  const [showAddPatientModal, setShowAddPatientModal] = useState<boolean>(false);
  const [selectedPatientOpt, setSelectedPatientOpt] = useState<any | null>(null);
  const { getEcwOrdersAPI, getEcwOrdersLoading, revertEcwOrderAPI, revertEcwOrderLoading } = useEcwOrders();
  const {
    getPatientAPI,
    getPatientLoading,
    createPatientAPI,
    createPatientLoading,
  } = usePatient();

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    setFieldTouched
  } = formik;

  const selectedOrder = useMemo(() => {
    if (!values.orderId || !ecwOrders?.orders?.length) return null;
    const selectedOrderInfo =
      ecwOrders?.orders?.find(
        (item: EcwOrder) => String(item?.immId) === String(values.orderId)
      ) || null;
    dispatch(setEcwSelectedOrderInfo(selectedOrderInfo));
    return selectedOrderInfo;
  }, [values.orderId, ecwOrders]);

  useEffect(() => {
    dispatch(setEcwOrderList(ecwOrders));
  }, [dispatch, ecwOrders]);

  useEffect(() => {
    setLoading?.(createPatientLoading || revertEcwOrderLoading);
  }, [createPatientLoading, revertEcwOrderLoading, setLoading]);

  useEffect(() => {
    if (touched.patID) {
      setFieldTouched("patID", false);
    }
  }, [values.patID]);

  useEffect(() => {
    if (touched.orderId) {
      setFieldTouched("orderId", false);
    }
  }, [values.orderId]);

  useEffect(() => {
    if (!ecwOrderRes || !selectedOrder) return;

    const { isOrderProcessed, ecwResponseMessage } = ecwOrderRes;

    setEcwOrders((prev) => ({
      ...prev,
      orders: prev.orders?.map((order) =>
        order.immId === selectedOrder.immId
          ? {
            ...order,
            colour: isOrderProcessed ? "green" : "red",
            isSuccess: isOrderProcessed,
            status: isOrderProcessed ? "Administered" : order.status,
            ecwResponseMessage: isOrderProcessed ? null : ecwResponseMessage,
          }
          : order
      ),
    }));
  }, [ecwOrderRes]);

  const patientForm = useFormik({
    initialValues: AddPatientFormInitialValues,
    validationSchema: patientValidationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const patientRes = await createPatientAPI({
          ...values,
          dob: values.dob ? values.dob.toISOString().split("T")[0] : "",
        });

        if (patientRes?.id) {
          getPatientOptions(patientRes?.patientCode, true);
          setShowAddPatientModal(false);
          resetForm();
          toast.success("Patient added successfully");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to add patient");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getPatientOptions = useCallback(
    async (searchText: string = "", isOptSelect?: boolean) => {
      handlePatientChange(null);
      handleOrderChange(null);
      handleResetForm(true);
      try {
        const res: any = await getPatientAPI({ searchText });
        if (res && _.isArray(res) && res.length > 0) {
          const patients = res.map((item) => ({
            value: item,
            label: `${item.lastName}, ${item.firstName} ${item.mi ?? ""} (${item.patientCode})`,
          }));
          if (isOptSelect && patients.length) {
            const newOpt = patients[0];
            handlePatientChange(newOpt);
          }
          return patients;
        } else {
          return [];
        }
      } catch (err: any) {
        console.error(err);
        return [];
      }
    },
    [getPatientAPI]
  );

  const getEcwOrdersList = useCallback(
    async (patientId: string) => {
      if (!patientId) {
        setEcwOrders({});
        return;
      }
      try {
        const res = await getEcwOrdersAPI({ id: patientId });
        setEcwOrders(res ?? {});
      } catch (err) {
        console.error(err);
        setEcwOrders({});
      }
    },
    [getEcwOrdersAPI]
  );

  const handlePatientChange = useCallback(
    (option: any) => {
      const patient = option?.value || null;

      setSelectedPatientOpt(option || null);

      if (patient) {
        if (patient.patientCode !== values.patID) {
          handleOrderChange(null);
          getEcwOrdersList(patient.id);
        }
      }

      const fields: Partial<EcwFormValues> = {
        patID: patient?.patientCode ?? "",
        patFName: patient?.firstName ?? "",
        patMI: patient?.mi ?? "",
        patLName: patient?.lastName ?? "",
        patDOB: patient?.dob ?? "",
        patGender: patient?.gender ?? "",
      };

      Object.entries(fields).forEach(([key, val]) => setFieldValue(key, val));
    },
    [setFieldValue, values.patID, getEcwOrdersList]
  );

  const toggleShowAddPatientModal = useCallback(() => {
    toggleClass();
    setShowAddPatientModal((prev) => {
      if (prev) {
        patientForm.resetForm();
      }
      return !prev;
    });
  }, [patientForm]);

  const handleOrderChange = useCallback(
    (selectedItem: any) => {
      const order = selectedItem?.value ?? null;

      const fields: Partial<EcwFormValues> = {
        orderId: order?.immId ?? "",
        orderType: order?.immId ? "Vaccine" : "",
        hcpid: order?.provider?.npi ?? "",
        hcpfName: order?.provider?.firstName ?? "",
        hcplName: order?.provider?.lastName ?? "",
        hcpNum: order?.provider?.deaNumber ?? "",
        location: order?.facility?.facilityName ?? "",
        encounterDate: order?.encounterDate ?? "",
        encounterId: order?.encounterId ?? "",
        facilityGuid: order?.facility?.guid ?? "",
      };

      Object.entries(fields).forEach(([key, val]) => setFieldValue(key, val));
    },
    [setFieldValue]
  );

  const handleRevertOrder = useCallback(async (orderId: string | number) => {
    if (!orderId) return;
    try {
      const revertRes = await revertEcwOrderAPI({ id: orderId });
      const revicedOrderInfo = revertRes?.result;
      if (revicedOrderInfo) {
        setEcwOrders((prev) => ({
          ...prev,
          orders: prev.orders?.map((order) =>
            order.immId === revicedOrderInfo.immId
              ? {
                ...order,
                colour: revicedOrderInfo?.colour,
                isSuccess: revicedOrderInfo?.isSuccess,
                status: revicedOrderInfo?.status,
              }
              : order
          ),
        }));
      }
    } catch (error) {
      console.error("Error occurred while reverting order:", error);
    }
  }, []);

  const loadPatientOptions = useMemo(() => {
    const debouncedFn = _.debounce(
      (inputValue: string, callback: (options: any[]) => void) => {
        getPatientOptions(inputValue).then(callback);
      },
      300
    );

    return (inputValue: string) =>
      new Promise<any[]>((resolve) => {
        debouncedFn(inputValue, resolve);
      });
  }, [getPatientOptions]);

  const ecwOrdersOptions = useMemo(() => {
    return (
      ecwOrders?.orders?.map((item: EcwOrder) => ({
        label: `${item?.encounterDate} - ${item?.vaccineName} (Enc. Id: ${item?.encounterId}, Order Id: ${item?.immId})`,
        value: item,
      })) || []
    );
  }, [ecwOrders]);

  const defaultPatientOptions = useMemo(() => {
    return selectedPatientOpt ? [selectedPatientOpt] : [];
  }, [selectedPatientOpt]);

  const isPermitted = useModulePermissions(PermissionModule.ECW_ORDER_ADMINISTRATION);
  const isAddPatientPermitted = isPermitted(PermissionIdentifier.Add_PATIENT_ON_ECW);

  return (
    <>
      <div className="basic-info-box">
        <h4 className="form-section-title">
          Basic Information
          <span
            className={`order-type-symbol ${OrderTypeLogos[values.orderType]}`}
          >
            {OrderTypeLogos[values.orderType]}
          </span>
        </h4>
        <Form.Group className="form-field">
          <Form.Label>
            Patient ID*{" "}
            {isAddPatientPermitted && (
              <Badge bg="primary" onClick={toggleShowAddPatientModal}>
                Add Patient
              </Badge>
            )}
          </Form.Label>
          <AsyncSelect
            cacheOptions={false}
            value={selectedPatientOpt}
            isLoading={getPatientLoading}
            classNamePrefix="react-select"
            placeholder="Enter Patient ID"
            loadOptions={loadPatientOptions}
            onChange={handlePatientChange}
            defaultOptions={defaultPatientOptions}
            components={{ DropdownIndicator: () => null }}
            className={`${touched.patID && errors.patID ? "is-invalid-border" : ""} react-select-container`}
          />
          {errors.patID && touched.patID && (
            <Form.Control.Feedback type="invalid">
              {errors.patID}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group className="form-field">
          <Form.Label>Order*</Form.Label>
          <Select
            name="orderId"
            options={ecwOrdersOptions}
            isLoading={getEcwOrdersLoading}
            classNamePrefix="react-select"
            placeholder="Please select order"
            onChange={handleOrderChange}
            value={
              ecwOrdersOptions.find(
                (opt) => String(opt.value.immId) === String(values.orderId)
              ) || null
            }
            className={`${touched.orderId && errors.orderId ? "is-invalid-border" : ""} react-select-container`}
          />
          {errors.orderId && touched.orderId && (
            <Form.Control.Feedback type="invalid">
              {errors.orderId}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group className="form-field">
          <Form.Label>Order Type</Form.Label>
          <div className="radio-group">
            {OrderTypeOptions.map((item) => (
              <Form.Check
                inline
                disabled
                key={item}
                type="radio"
                id={`orderType-${item}`}
                name="orderType"
                label={item}
                value={item}
                onChange={handleChange}
                checked={values.orderType === item}
              />
            ))}
          </div>
        </Form.Group>

        <EcwOrderInfo
          ecwOrderList={ecwOrders}
          selectedOrderInfo={selectedOrder}
          handleRevertOrder={handleRevertOrder}
        />

        {recordApiMsgs && (
          <ul className="d-flex flex-column">
            {recordApiMsgs.success && (
              <li className="text-success fw-semibold">
                {ensurePeriod(recordApiMsgs.success)}
              </li>
            )}
            {recordApiMsgs.error?.map((err, index) => (
              <li key={index} className="text-danger fw-semibold">
                {ensurePeriod(err)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddPatientModal && (
        <CustomModal
          id="addpatientcmodal"
          title="Add Patient"
          content={
            <div className="add-patient-details">
              <Form noValidate onSubmit={patientForm.handleSubmit}>
                <Form.Group className="form-field">
                  <Form.Label>
                    Patient ID<sup>*</sup>
                  </Form.Label>
                  <Form.Control
                    id="patientCode"
                    name="patientCode"
                    type="text"
                    autoFocus
                    placeholder="Enter Patient ID"
                    onChange={patientForm.handleChange}
                    value={patientForm.values.patientCode}
                    isInvalid={
                      patientForm.touched.patientCode &&
                      !!patientForm.errors.patientCode
                    }
                  />
                  {patientForm.touched.patientCode &&
                    patientForm.errors.patientCode ? (
                    <Form.Control.Feedback type="invalid">
                      {patientForm.errors.patientCode}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>
                <Form.Group className="form-field">
                  <Form.Label>
                    First Name<sup>*</sup>
                  </Form.Label>
                  <Form.Control
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="Enter First Name"
                    onChange={patientForm.handleChange}
                    value={patientForm.values.firstName}
                    isInvalid={
                      patientForm.touched.firstName &&
                      !!patientForm.errors.firstName
                    }
                  />
                  {patientForm.touched.firstName &&
                    patientForm.errors.firstName ? (
                    <Form.Control.Feedback type="invalid">
                      {patientForm.errors.firstName}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>
                <Form.Group className="form-field">
                  <Form.Label>
                    Last Name<sup>*</sup>
                  </Form.Label>
                  <Form.Control
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Enter Last Name"
                    onChange={patientForm.handleChange}
                    value={patientForm.values.lastName}
                    isInvalid={
                      patientForm.touched.lastName &&
                      !!patientForm.errors.lastName
                    }
                  />
                  {patientForm.touched.lastName &&
                    patientForm.errors.lastName ? (
                    <Form.Control.Feedback type="invalid">
                      {patientForm.errors.lastName}
                    </Form.Control.Feedback>
                  ) : null}
                </Form.Group>
                <Form.Group className="form-field">
                  <Form.Label>DOB</Form.Label>
                  <div className="calender-box">
                    <DatePicker
                      locale="en-US"
                      format="MM/dd/yyyy"
                      dayPlaceholder="dd"
                      monthPlaceholder="mm"
                      yearPlaceholder="yyyy"
                      clearIcon={null}
                      showLeadingZeros={true}
                      openCalendarOnFocus={false}
                      value={patientForm.values.dob}
                      calendarIcon={<i className="bi bi-calendar"></i>}
                      onChange={(date) =>
                        patientForm.setFieldValue("dob", date)
                      }
                      className={`${patientForm.touched.dob && !!patientForm.errors.dob ? "is-invalid-border" : ""} form-control`}
                    />
                  </div>
                  {patientForm.touched.dob && patientForm.errors.dob ? (
                    <div className="invalid-feedback d-block">
                      {patientForm.errors.dob}
                    </div>
                  ) : null}
                </Form.Group>
                <Form.Group className="form-field">
                  <Form.Label>
                    Gender<sup>*</sup>
                  </Form.Label>
                  <div className="item-radio-content">
                    {genderOptions.map((gender) => (
                      <Form.Check
                        key={gender}
                        inline
                        type="radio"
                        id={`gender-${gender}`}
                        name="gender"
                        label={gender}
                        value={gender}
                        onChange={patientForm.handleChange}
                        checked={patientForm.values.gender === gender}
                        isInvalid={
                          patientForm.touched.gender &&
                          !!patientForm.errors.gender
                        }
                      />
                    ))}
                    {patientForm.touched.gender && patientForm.errors.gender ? (
                      <div className="invalid-feedback d-block">
                        {patientForm.errors.gender}
                      </div>
                    ) : null}
                  </div>
                </Form.Group>
                <div className="form-actions">
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={toggleShowAddPatientModal}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={patientForm.isSubmitting || !isAddPatientPermitted}>
                    Add
                  </Button>
                </div>
              </Form>
            </div>
          }
          onClose={toggleShowAddPatientModal}
        />
      )}
    </>
  );
};

export default EcwBasicInfoForm;
